'use client';
import cn from 'classnames';
import styles from './ProductInfo.module.scss';
import { Rating } from '@/atoms/Rating';
import { parseText } from '@/utils/parseText';
import { ProductDetailedType } from '@/types';
import { LinkPrimary } from '@/atoms/LinkPrimary';
import { Link } from '@/i18n/navigation';
import { Size } from '@/atoms/Size';
import { Button } from '@/atoms/Button';
import { LikeButton } from '@/atoms/LikeButton';
import { ProductDescriptionBlock } from '@/modules/Product';
import { AccordionBlock } from '@/atoms/AccordionBlock';
import { getBackendImage } from '@/utils/getBackendImage';
import { useState } from 'react';
import { DeliveryServiceDescription } from '@/atoms/DeliveryServiceDescription';
import { useRouter } from 'next/navigation';
import { BottomPanel } from '@/atoms/BottomPanel';
import { useSelector } from 'react-redux';
import { selectFavourites, selectUid } from '@/store';
import { formatPrice } from '@/utils/formatPrice';
import { SizesTable } from '@/modules/Product/SizesTable';
import { useCartData } from '@/hooks/useCartData';
import toast from 'react-hot-toast';
import { useControlFavourites } from '@/hooks/useControlFavourites';
import { KitItem } from '@/atoms/KitItem';
import { GetInStockInfoModal } from '@/components/GetInStockInfoModal';

type ProductInfoProps = {
  className?: string;
  product: ProductDetailedType;
};

export function ProductInfo({ className, product }: ProductInfoProps) {
  const wrapperClassName = cn(styles.info, className);
  const defaultOffer = product.offers.length > 0 ? product.offers[0] : null;
  const [chosenOffer, setChosenOffer] = useState(defaultOffer);
  const { push } = useRouter();
  const uid = useSelector(selectUid);
  const [sizesTableActive, setSizesTableActive] = useState(false);
  const { addOneItem } = useCartData();
  const { addToFavourites, removeFromFavourites } = useControlFavourites();
  const favourites = useSelector(selectFavourites);
  const isLiked = favourites.includes(product.id);
  const showSizesBlock = product.tableSize || product.offers.length > 1;
  const noOffers = !product.offers || product.offers?.length === 0;
  const productOutOfStock =
    product.quantity === 0 && (!chosenOffer || chosenOffer.quantity === 0);
  const [showGetInStockModal, setShowGetInStockModal] = useState(false);

  const handleClickRating = () => {
    document
      .getElementById('reviews')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleClickBrand = () => {
    push(`${product.brand.link}`);
  };

  const handleAddToCart = async () => {
    if (!uid) return;

    if (product.quantity > 0) {
      addOneItem(product.id);
      return;
    }

    if (noOffers) {
      toast.error('Товар закончился');
      return;
    }

    if (chosenOffer?.quantity === 0) {
      toast.error('Товар закончился');
      return;
    }

    if (!chosenOffer) {
      toast.error('Товар закончился');
      return;
    }

    addOneItem(chosenOffer?.id);
  };

  const handleLikeClick = () => {
    if (isLiked) {
      removeFromFavourites(product.id);
    } else {
      addToFavourites({
        id: product.id,
        name: product.name,
        url: product.url,
        picture: product.pictures?.[0] ?? '',
        brand: product.brand?.name ?? '',
        price: product.price,
        oldPrice: product.oldPrice,
      });
    }
  };

  const showSizesTable = () => {
    setSizesTableActive(true);
  };

  const closeSizesTable = () => {
    setSizesTableActive(false);
  };

  const handleGetInStockInfo = () => {
    setShowGetInStockModal(true);
  };

  return (
    <div className={wrapperClassName}>
      <div className={styles.mainContainer}>
        <div className={styles.priceMobile}>
          <div className={styles.newPrice}>{formatPrice(product.price)} ₽</div>
          {product.oldPrice && product.oldPrice !== product.price && (
            <div className={styles.oldPrice}>
              {formatPrice(product.oldPrice)}
            </div>
          )}
        </div>
        <div className={styles.main}>
          <Rating
            rating={product.rating}
            reviewsCount={product.countReviews}
            isBig
            onClick={handleClickRating}
          />
          <div className={styles.titles}>
            <div className={styles.brand} onClick={handleClickBrand}>
              {parseText(product.brand?.name)}
            </div>
            <div className={styles.name}>{parseText(product?.name)}</div>
          </div>
        </div>
      </div>
      <div className={styles.price}>
        <div className={styles.newPrice}>{formatPrice(product.price)} ₽</div>
        {product.oldPrice && product.oldPrice !== product.price && (
          <div className={styles.oldPrice}>{formatPrice(product.oldPrice)}</div>
        )}
      </div>
      {showSizesBlock && (
        <div className={styles.sizesContainer}>
          <div className={styles.sizes}>
            {product.offers.map((item) => (
              <Size
                key={item.id}
                size={item.size}
                isActive={item.id === chosenOffer?.id}
                onClick={() => setChosenOffer(item)}
                isUnavailable={item.quantity === 0}
              />
            ))}
          </div>
          {product.tableSize && (
            <LinkPrimary
              href=""
              text="Таблица размеров"
              onClick={showSizesTable}
            />
          )}
          {product.tableSize && (
            <SizesTable
              data={product.tableSize}
              active={sizesTableActive}
              onClose={closeSizesTable}
            />
          )}
        </div>
      )}
      {product.color && (
        <div className={styles.color}>
          <span className={styles.colorName}>Цвет:</span>
          <span className={styles.colorValue}>{product.color}</span>
        </div>
      )}
      {product.otherColors && (
        <div className={styles.variants}>
          <div className={styles.otherProducts}>
            {product.otherColors.map((item, index) => (
              <Link
                href={item.url}
                key={index}
                className={cn(styles.otherProduct, {
                  [styles.currentProduct]: item.url === product.link,
                })}
              >
                <img
                  src={getBackendImage(item.picture)}
                  alt="other"
                  className={styles.otherProductImg}
                />
              </Link>
            ))}
          </div>
        </div>
      )}
      {product.otherCompleteness && (
        <div className={styles.kits}>
          {product.otherCompleteness.map((item, index) => (
            <KitItem
              key={index}
              kit={item}
              isActive={item.id === product.id}
              isUnavailable={false}
            />
          ))}
        </div>
      )}
      <div className={styles.actions}>
        {!productOutOfStock && (
          <Button
            variant="primary"
            text="В корзину"
            onClick={handleAddToCart}
            className={styles.cartButton}
            icon="/svg/cart.svg"
          />
        )}
        {productOutOfStock && (
          <Button
            variant="primary"
            text="Узнать о поступлении"
            onClick={handleGetInStockInfo}
            className={styles.cartButton}
          />
        )}
        <LikeButton onClick={handleLikeClick} active={isLiked} />
      </div>
      <div className={styles.accordions}>
        <ProductDescriptionBlock
          characteristics={product.characteristics}
          productId={product.id}
        />
        {product.careProduct && (
          <AccordionBlock title="Уход за изделием">
            <div className={styles.accordionContent}>
              <p
                className={styles.text}
                dangerouslySetInnerHTML={{ __html: product.careProduct.text }}
              />
            </div>
          </AccordionBlock>
        )}
        {product.returnProduct.text && (
          <AccordionBlock title="Условия возврата">
            <div className={styles.accordionContent}>
              <p
                className={styles.text}
                dangerouslySetInnerHTML={{ __html: product.returnProduct.text }}
              />
            </div>
          </AccordionBlock>
        )}
        {product.delivery && (
          <AccordionBlock title="Условия доставки">
            <div className={styles.accordionContent}>
              {product.delivery.map((item, index) => (
                <DeliveryServiceDescription key={index} delivery={item} />
              ))}
            </div>
          </AccordionBlock>
        )}
        <BottomPanel>
          <div className={styles.buttonMobile}>
            {!productOutOfStock && (
              <Button
                onClick={handleAddToCart}
                text="В корзину"
                variant="primary"
                icon="/svg/cart.svg"
              />
            )}
            {productOutOfStock && (
              <Button
                onClick={handleGetInStockInfo}
                text="Узнать о поступлении"
                variant="primary"
              />
            )}
          </div>
        </BottomPanel>
      </div>
      <GetInStockInfoModal
        active={showGetInStockModal}
        onClose={() => setShowGetInStockModal(false)}
        productId={product.id}
      />
    </div>
  );
}
